/**
 * Ticket #39 — persistance de l'écran courant dans l'URL.
 *
 * Synolog est une SPA sans router : l'écran actif est un simple `useState` dans
 * `App.jsx`. Conséquence, un CTRL+R repartait systématiquement sur l'accueil.
 * Plutôt que d'embarquer react-router pour une navigation à un seul niveau, on
 * mappe ici la clé de vue sur un slug d'URL et on pilote `history` à la main.
 *
 * Convention (validée ticket #39) : slugs anglais alignés sur les clés de vue,
 * `home` étant la racine `/`. Ces slugs deviennent des URLs publiques
 * (bookmarks, liens partagés) : les considérer comme un contrat, ne pas les
 * renommer même si la clé interne bouge — ajouter un alias dans `LEGACY_SLUGS`.
 *
 * ⚠ Le `next` du SSO core est whitelisté côté API par
 * `SAFE_NEXT_PATTERN = /^\/[a-zA-Z0-9/_-]*$/` (api/src/lib/oauthState.ts) : tout
 * slug doit rester dans cet alphabet, sinon le retour de login retombe sur `/`.
 */

const HOME_KEY = 'home';

// Anciens chemins éventuels → clé de vue. Vide aujourd'hui, c'est le point
// d'extension quand un slug public doit être renommé sans casser les bookmarks.
const LEGACY_SLUGS = {};

/** Slug public d'une clé de vue. `home` → `/`, sinon `/<key>`. */
export function pathForViewKey(key) {
  if (!key || key === HOME_KEY) return '/';
  return `/${key}`;
}

/**
 * Clé de vue portée par un pathname, ou `null` si le chemin ne correspond à
 * aucune vue connue (URL exotique, ancien lien) — l'appelant retombe alors sur
 * l'accueil.
 *
 * @param {string} pathname       ex. `/schedule`
 * @param {Set<string>|Map<string, unknown>} knownKeys clés de vue existantes
 */
export function viewKeyFromPath(pathname, knownKeys) {
  const raw = (pathname || '/').replace(/\/+$/, '');
  if (raw === '' || raw === '/') return HOME_KEY;
  const slug = raw.startsWith('/') ? raw.slice(1) : raw;
  // Un slug à plusieurs segments n'est pas une vue (ex. asset servi en fallback).
  if (slug.includes('/')) return null;
  const key = LEGACY_SLUGS[slug] ?? slug;
  return knownKeys.has(key) ? key : null;
}

/**
 * Aligne l'URL sur la vue active sans recharger la page. `replace: true` pour
 * les corrections silencieuses (vue interdite, chemin inconnu) afin de ne pas
 * polluer l'historique avec une entrée que l'utilisateur n'a jamais demandée.
 */
export function syncUrlToView(key, { replace = false } = {}) {
  const path = pathForViewKey(key);
  // On conserve la query string : `?view=<id>` (lien de vue sauvegardée,
  // ticket #37) et les params consommés par useAuth doivent survivre.
  const url = path + window.location.search + window.location.hash;
  if (window.location.pathname === path && !replace) return;
  const method = replace ? 'replaceState' : 'pushState';
  window.history[method](null, '', url);
}

/**
 * synotis-support#34 — lien profond vers le rapport d'une affaire
 * (Rapports › Projets, détail ouvert par `?project=<id>`).
 */
export const PROJECTS_REPORT_KEY = 'reports-projects';
export function projectReportHref(affaireId) {
  return `${pathForViewKey(PROJECTS_REPORT_KEY)}?project=${encodeURIComponent(affaireId)}`;
}

/**
 * Ouvre le rapport d'une affaire sans recharger la page. L'URL complète
 * (`/reports-projects?project=<id>`) est poussée AVANT le changement de vue :
 * Précédent ramène à l'écran d'origine, un rechargement rouvre l'affaire, et
 * la vue lit `?project=` à son montage. La query string de l'écran d'origine
 * n'est pas reprise (elle ne concerne pas le rapport).
 *
 * @param {string} affaireId
 * @param {(key: string, opts?: {replace?: boolean}) => void} navigate `onNavigate` du shell
 */
export function openProjectReport(affaireId, navigate) {
  window.history.pushState(null, '', projectReportHref(affaireId));
  navigate(PROJECTS_REPORT_KEY, { replace: true });
}

/**
 * Chemin à repasser en `next` au SSO pour revenir sur l'écran courant après
 * login. La query string est volontairement écartée : `safeNext` la rejetterait
 * et le retour retomberait sur `/`.
 */
export function currentPathForLogin() {
  return window.location.pathname || '/';
}
